import { condString, type Nullable } from '@silverhand/essentials';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import type { HTMLProps, ReactElement, Ref } from 'react';
import {
  forwardRef,
  cloneElement,
  useState,
  useImperativeHandle,
  useRef,
  useEffect,
  useId,
} from 'react';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '@/shared/components/ErrorMessage';

import styles from './index.module.scss';

export type Props = Omit<HTMLProps<HTMLInputElement>, 'prefix'> & {
  readonly className?: string;
  readonly inputFieldClassName?: string;
  readonly errorMessage?: string;
  readonly isDanger?: boolean;
  readonly prefix?: ReactElement;
  readonly isPrefixVisible?: boolean;
  readonly suffix?: ReactElement;
  readonly isSuffixFocusVisible?: boolean;
  readonly label?: ReactElement | string;
  readonly description?: Nullable<string>;
};

const InputField = (
  {
    className,
    inputFieldClassName,
    errorMessage,
    isDanger,
    prefix,
    suffix,
    isPrefixVisible,
    isSuffixFocusVisible,
    label,
    description,
    onFocus,
    onBlur,
    onChange,
    value,
    required = true,
    id: idProp,
    ...props
  }: Props,
  reference: Ref<Nullable<HTMLInputElement>>
) => {
  const { t, i18n } = useTranslation();
  const innerRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const id = idProp ?? generatedId;

  useImperativeHandle(reference, () => innerRef.current);

  const errorMessages = errorMessage?.split('\n');

  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  useEffect(() => {
    setHasValue(Boolean(value));
  }, [value]);

  const showSuffix =
    suffix &&
    isSuffixFocusVisible &&
    (Boolean(isPrefixVisible) || hasValue || isFocused);
  console.log(showSuffix, isSuffixFocusVisible, Boolean(isPrefixVisible), hasValue, isFocused);
  

  const labelWithOptionalSuffix = required
    ? label
    : condString(label && t('input.label_with_optional', { label }));

  return (
    <div className={className}>
      {labelWithOptionalSuffix != null &&
        (typeof labelWithOptionalSuffix === 'string' ? (
          <Label htmlFor={id} className={cn(styles.label, 'mb-2 text-card-foreground')}>
            {labelWithOptionalSuffix}
          </Label>
        ) : (
          <div className={styles.label}>{labelWithOptionalSuffix}</div>
        ))}
      <InputGroup
        className={cn(
          inputFieldClassName,
          isDanger && 'border-destructive has-[[data-slot=input-group-control]]:ring-destructive/20',
        )}
        dir={i18n.dir()}
      >
        {prefix != null && (
          <InputGroupAddon align="inline-start">{prefix}</InputGroupAddon>
        )}
        <Input
          data-slot="input-group-control"
          {...props}
          ref={innerRef}
          id={id}
          value={value}
          aria-invalid={isDanger ? true : undefined}
          className={cn(
            'flex-1 rounded-none border-0 bg-transparent shadow-none focus:shadow-none focus:border-transparent focus-visible:shadow-none focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent ring-offset-transparent dark:bg-transparent',
          )}
          onFocus={(event) => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          onChange={(event) => {
            setHasValue(Boolean(event.target.value));
            onChange?.(event);
          }}
        />
        {showSuffix && (
          <InputGroupAddon align="inline-end">
            {cloneElement(suffix, {
              className: cn(suffix.props.className),
            })}
          </InputGroupAddon>
        )}
      </InputGroup>
      {description != null && (
        <div className={styles.description}>{description}</div>
      )}
      {errorMessages != null && errorMessages.length > 0 && (
        <ErrorMessage className={styles.errorMessage}>
          {errorMessages.length > 1 ? (
            <ul>
              {errorMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : (
            errorMessages[0]
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default forwardRef(InputField);
